import React, { ReactNode } from 'react';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { NOTIFICATIONS } from '../constants/styles';
import { generateNotificationId } from './notificationHelpers';
import { NotificationAction, NotificationsActionType } from './notificationsActionType';
import { NotificationType } from './notificationType.enum';

export class NotificationsActions {
    public static generateShowNotificationAction(
        notificationMessage: ReactNode,
        notificationType: NotificationType,
        notificationId: string = generateNotificationId(),
        notificationTitle?: ReactNode,
    ): NotificationAction {
        return {
            payload: {
                notification: {
                    blockUI: false,
                    id: notificationId,
                    message: notificationMessage,
                    nonDismissable: false,
                    type: notificationType,
                    title: notificationTitle,
                },
            },
            type: NotificationsActionType.ADD_NOTIFICATION,
        };
    }

    public static showRedirectWarning(redirectUrl: string, reasonContentId: string): any {
        return (dispatch: Dispatch<NotificationAction>) => {
            const id = generateNotificationId();
            let count = 3;

            const interval = setInterval(() => {
                count--;
                if (count >= 0) {
                    dispatch({
                        payload: {
                            notificationId: id,
                            notificationMessage: NotificationsActions.generateRedirectMessage(
                                redirectUrl,
                                reasonContentId,
                                count,
                            ),
                        },
                        type: NotificationsActionType.UPDATE_NOTIFICATION_MESSAGE,
                    });
                } else {
                    clearInterval(interval);
                }
            }, 1000);

            dispatch({
                payload: {
                    notification: {
                        blockUI: true,
                        id,
                        message: NotificationsActions.generateRedirectMessage(
                            redirectUrl,
                            reasonContentId,
                            count,
                        ),
                        nonDismissable: true,
                        type: NotificationType.Redirecting,
                    },
                },
                type: NotificationsActionType.ADD_NOTIFICATION,
            });
        };
    }

    public static showRedirectSuccess(reasonContentId: string): any {
        return (dispatch: Dispatch<NotificationAction>) => {
            const id = generateNotificationId();
            let count = 5;

            setTimeout(() => {
                dispatch(NotificationsActions.removeNotification(id));
            }, NOTIFICATIONS[NotificationType.Success_Redirecting].expiry);

            const interval = setInterval(() => {
                count--;
                if (count >= 0) {
                    dispatch({
                        payload: {
                            notificationId: id,
                            notificationMessage:
                                NotificationsActions.generateSuccessRedirectMessage(
                                    reasonContentId,
                                    count,
                                ),
                        },
                        type: NotificationsActionType.UPDATE_NOTIFICATION_MESSAGE,
                    });
                } else {
                    clearInterval(interval);
                }
            }, 1000);

            dispatch({
                payload: {
                    notification: {
                        blockUI: false,
                        id,
                        message: NotificationsActions.generateSuccessRedirectMessage(
                            reasonContentId,
                            count,
                        ),
                        nonDismissable: false,
                        type: NotificationType.Success_Redirecting,
                    },
                },
                type: NotificationsActionType.ADD_NOTIFICATION,
            });
        };
    }

    public static showSuccess(message: ReactNode): any {
        const id = generateNotificationId(message);

        return (dispatch: Dispatch<NotificationAction>) => {
            setTimeout(() => {
                dispatch(NotificationsActions.removeNotification(id));
            }, NOTIFICATIONS[NotificationType.Success].expiry);

            dispatch(this.generateShowNotificationAction(message, NotificationType.Success, id));
        };
    }

    public static showWarning(message: ReactNode): any {
        const id = generateNotificationId(message);

        return (dispatch: Dispatch<NotificationAction>) => {
            setTimeout(() => {
                dispatch(NotificationsActions.removeNotification(id));
            }, NOTIFICATIONS[NotificationType.Warning].expiry);
            dispatch(this.generateShowNotificationAction(message, NotificationType.Warning, id));
        };
    }

    public static showError(message: ReactNode, title?: ReactNode): any {
        const id = generateNotificationId(message);

        return (dispatch: Dispatch<NotificationAction>) => {
            setTimeout(() => {
                dispatch(NotificationsActions.removeNotification(id));
            }, NOTIFICATIONS[NotificationType.Error].expiry);
            dispatch(
                this.generateShowNotificationAction(message, NotificationType.Error, id, title),
            );
        };
    }

    public static removeNotification(notificationId: string): NotificationAction {
        return {
            payload: { notificationId },
            type: NotificationsActionType.REMOVE_NOTIFICATION,
        };
    }

    public static generateRedirectMessage(
        redirectUrl: string,
        reasonContentId: string,
        count: number,
    ): ReactNode {
        const Link = styled.a`
            &,
            &:active,
            &:focus,
            &:hover {
                color: inherit;
                font-weight: 500;
                text-decoration: underline;
            }
        `;

        return (
            <div>
                {TranslationHelpers.getFormattedMessage(reasonContentId)}
                {TranslationHelpers.getFormattedMessage('notifications.redirect.action')}
                {TranslationHelpers.getFormattedMessage(
                    `notifications.redirect.time.count${count}`,
                )}
                {TranslationHelpers.getFormattedMessage('notifications.redirect.otherwise', {
                    here: (
                        <Link href={redirectUrl}>
                            {TranslationHelpers.getFormattedMessage('notifications.redirect.here')}
                        </Link>
                    ),
                })}
            </div>
        );
    }

    public static generateSuccessRedirectMessage(
        reasonContentId: string,
        count: number,
    ): ReactNode {
        const Timer = styled.span`
            font-weight: 600;
        `;
        return (
            <div>
                {TranslationHelpers.getFormattedMessage(reasonContentId, {
                    count: <Timer>{count}</Timer>,
                })}
            </div>
        );
    }
}
